import React, { useEffect, useState } from 'react'
import { RssIcon, ChatAlt2Icon } from '@heroicons/react/solid'

import useQuery from '@/hooks/useQuery'
import Loading from '@/shared/Loading'
import { useGlobalState } from '@/state'
import ReactionButton from '@/shared/Buttons/ReactionButton'

const ChangelogEntry = ({ entry, currentUser, userReactions }) => {
  const [expanded, setExpanded] = useState(false)

  return (
    <article className='group relative rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow'>
      {entry.coverImageUrl && (
        <div className='relative overflow-hidden rounded-t-2xl'>
          <img
            src={entry.coverImageUrl}
            alt=''
            className='w-full h-48 sm:h-56 object-cover'
            loading='lazy'
          />
          <div className='absolute inset-0 bg-gradient-to-t from-black/30 to-transparent' />
        </div>
      )}

      <div className='p-5 sm:p-6'>
        <time className='block text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-3'>
          {entry.publishedAtLabel}
        </time>

        <h3 className='text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-3 leading-tight'>
          <a href={`#${entry.slug}`} id={entry.slug} className='hover:text-cccblue transition-colors scroll-mt-20'>
            {entry.title}
          </a>
        </h3>

        <div className={`relative ${!expanded && !entry.coverImageUrl ? 'max-h-40 overflow-hidden' : ''}`}>
          <div
            className='prose prose-sm dark:prose-invert max-w-none text-gray-600 dark:text-gray-300 leading-relaxed changelog-body'
            dangerouslySetInnerHTML={{ __html: entry.bodyHtml }}
          />
          {!expanded && !entry.coverImageUrl && (
            <div className='absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white dark:from-gray-800 to-transparent' />
          )}
        </div>

        {!expanded && !entry.coverImageUrl && (
          <button
            onClick={() => setExpanded(true)}
            className='mt-2 text-sm font-medium text-cccblue hover:text-cccblue-alt transition-colors'
          >
            Read more
          </button>
        )}

        {/* Reactions — logged in only */}
        {currentUser?.id && (
          <div className='mt-4 pt-3 border-t border-gray-100 dark:border-gray-700'>
            <ReactionButton
              reactionsData={entry.reactions || {}}
              userReactions={userReactions}
              reactionableId={entry.id}
              reactionableType='ChangelogEntry'
            />
          </div>
        )}

        {/* Static reaction counts — logged out */}
        {!currentUser?.id && entry.reactions && Object.keys(entry.reactions).length > 0 && (
          <div className='mt-4 pt-3 border-t border-gray-100 dark:border-gray-700'>
            <div className='flex gap-2'>
              {entry.reactions.Like > 0 && <span className='text-sm text-gray-500'>👍 {entry.reactions.Like}</span>}
              {entry.reactions.Seen > 0 && <span className='text-sm text-gray-500'>👀 {entry.reactions.Seen}</span>}
              {entry.reactions.Nope > 0 && <span className='text-sm text-gray-500'>👎 {entry.reactions.Nope}</span>}
              {entry.reactions.Rice > 0 && <span className='text-sm text-gray-500'>🍚 {entry.reactions.Rice}</span>}
            </div>
          </div>
        )}
      </div>
    </article>
  )
}

const ChangelogIndex = () => {
  const { getRequest } = useQuery()
  const [currentUser] = useGlobalState('currentUser')
  const [entries, setEntries] = useState(null)
  const [userReactions, setUserReactions] = useState([])

  useEffect(() => {
    getRequest('/api/v3/changelog_entries', {}, (err, jsonData) => {
      if (err) { return }
      setEntries(jsonData.entries || [])
      setUserReactions(jsonData.userReactions || [])
    })
  }, [])

  if (entries === null) { return <Loading /> }

  return (
    <div className='max-w-3xl mx-auto'>
      <div className='flex justify-between items-start mb-8'>
        <p className='text-gray-500 dark:text-gray-400 text-sm max-w-lg'>
          Product updates, release notes, and the occasional apology.
        </p>
        <a
          href='/changelog.rss'
          className='flex-shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors'
          title='RSS feed'
        >
          <RssIcon className='h-4 w-4 text-cccorange' />
          Subscribe
        </a>
      </div>

      <a
        href='/feedback'
        className='block mb-6 rounded-2xl bg-gradient-to-br from-cccpurple to-cccblue p-5 sm:p-6 text-white shadow-md hover:shadow-lg transition-shadow'
      >
        <div className='flex items-center justify-between gap-4'>
          <div>
            <p className='text-xs font-semibold uppercase tracking-wider opacity-90'>Got an idea? Found a bug?</p>
            <h2 className='text-lg sm:text-xl font-bold mt-1'>Tell us on the feedback board →</h2>
          </div>
          <ChatAlt2Icon className='h-10 w-10 sm:h-12 sm:w-12 opacity-80 flex-shrink-0' />
        </div>
      </a>

      {entries.length === 0 && (
        <p className='text-sm text-center italic text-gray-500 dark:text-gray-400 py-12'>Nothing published yet.</p>
      )}

      <div className='flex flex-col gap-5'>
        {entries.map((entry) => (
          <ChangelogEntry key={entry.id} entry={entry} currentUser={currentUser} userReactions={userReactions} />
        ))}
      </div>
    </div>
  )
}

export default ChangelogIndex
